import { useEffect, useState, useRef } from "react";
import { apiClient } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Sparkles, Download, Dumbbell, Apple, Loader2, Lock, History, ChevronDown, Flame, ShoppingCart, Shuffle, Minus, Plus, Mail, FileDown, CheckSquare, Square, ClipboardList } from "lucide-react";
import DisclaimerBanner from "@/components/DisclaimerBanner";
import { useAuth } from "@/context/AuthContext";

const GOAL_FR = { perte_poids: "Perte de poids", prise_muscle: "Prise de muscle", remise_forme: "Remise en forme" };

export default function ProgramView({ reloadKey, hasQuestionnaire = true }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [history, setHistory] = useState([]);
  const [openCycle, setOpenCycle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("entrainement");
  const [week, setWeek] = useState(0);
  const [day, setDay] = useState(0);
  const pollRef = useRef(null);
  const [mealScales, setMealScales] = useState({});
  const [mealOverrides, setMealOverrides] = useState({});
  const [subLoading, setSubLoading] = useState(null);
  const [checkedItems, setCheckedItems] = useState({});
  const [lockedWeek, setLockedWeek] = useState(null);

  const scaleGram = (g, f) => {
    const m = String(g).match(/^\s*([\d.,]+)\s*(.*)$/);
    if (!m) return g;
    const n = parseFloat(m[1].replace(",", "."));
    if (isNaN(n)) return g;
    return `${Math.round(n * f)} ${m[2]}`.trim();
  };
  const mkey = (mi) => `${day}-${mi}`;
  const getScale = (mi) => mealScales[mkey(mi)] ?? 1;
  const setScale = (mi, f) => setMealScales((s) => ({ ...s, [mkey(mi)]: Math.min(2, Math.max(0.5, +f.toFixed(2))) }));
  const getMeal = (mi, original) => mealOverrides[mkey(mi)] || original;

  const substituteMeal = async (mi, meal) => {
    setSubLoading(mkey(mi));
    try {
      const { data } = await apiClient.post("/nutrition/substitute-meal", { meal_type: meal.type, kcal_total: meal.kcal_total });
      setMealOverrides((o) => ({ ...o, [mkey(mi)]: data }));
      setMealScales((s) => ({ ...s, [mkey(mi)]: 1 }));
      toast.success("Repas remplacé ✨");
    } catch (e) { toast.error("Impossible de remplacer le repas."); }
    finally { setSubLoading(null); }
  };

  const allShoppingItems = () => (program?.plan?.nutrition?.liste_courses || []).flatMap((c) => c.aliments || []);
  const toggleItem = (item) => setCheckedItems((c) => ({ ...c, [item]: !c[item] }));
  const exportShopping = () => {
    const txt = "Liste de courses Pousse\n\n" + allShoppingItems().map((i) => (checkedItems[i] ? "[x] " : "[ ] ") + i).join("\n");
    const blob = new Blob([txt], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "liste-courses-pousse.txt"; a.click();
    URL.revokeObjectURL(url);
    toast.success("Liste téléchargée 📄");
  };
  const emailShopping = async () => {
    try {
      await apiClient.post("/shopping/email", { items: allShoppingItems() });
      toast.success("Liste envoyée par email ! 📩");
    } catch (e) { toast.error("Échec de l'envoi."); }
  };

  const refreshHistory = async () => {
    try {
      const all = await apiClient.get("/programs");
      setHistory((all.data || []).filter((p) => p.status === "archived"));
    } catch (e) {}
  };

  const startPolling = () => {
    setGenerating(true);
    let attempts = 0;
    const tick = async () => {
      attempts++;
      try {
        const { data } = await apiClient.get("/programs/latest");
        if (data?.status === "active" && data.plan) {
          setProgram(data); setWeek(0); setDay(0); setGenerating(false);
          refreshHistory(); toast.success("Ton programme est prêt ! 🎉"); return;
        }
        if (data?.status === "failed" || attempts > 50) {
          setGenerating(false); toast.error("La génération a échoué. Réessaie."); return;
        }
      } catch (e) {}
      pollRef.current = setTimeout(tick, 3000);
    };
    tick();
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/programs/latest");
      if (data?.status === "active" && (data.plan || data.access === "expired")) { setProgram(data); }
      else { setProgram(null); if (data?.status === "generating") startPolling(); }
      refreshHistory();
    } catch (e) { setProgram(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); return () => clearTimeout(pollRef.current); }, [reloadKey]);

  const generate = async () => {
    try {
      await apiClient.post("/programs/generate");
      startPolling();
    } catch (err) {
      const status = err.response?.status, detail = err.response?.data?.detail;
      if (status === 402) { toast.error(detail); navigate("/pricing"); }
      else if (status === 400) { toast.error(detail); navigate("/questionnaire"); }
      else toast.error(detail || "Erreur de génération.");
    }
  };

  const downloadPdf = () => {
    const p = program.plan, t = p.targets || {};
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const M = 40, W = 515, BOTTOM = 800;
    const GREEN = [22, 101, 52], LIME = [200, 255, 0], INK = [30, 30, 30];
    const GOAL_FR = { perte_poids: "Perte de poids", prise_muscle: "Prise de muscle", remise_forme: "Remise en forme" };
    let y = 50;

    const tableOpts = {
      theme: "grid",
      headStyles: { fillColor: GREEN, textColor: 255, fontStyle: "bold", fontSize: 10 },
      bodyStyles: { fontSize: 9, textColor: INK },
      alternateRowStyles: { fillColor: [244, 250, 238] },
      styles: { cellPadding: 5, lineColor: [223, 223, 223], lineWidth: 0.5, overflow: "linebreak", font: "helvetica" },
      margin: { left: M, right: M },
      pageBreak: "avoid",
      rowPageBreak: "avoid",
    };
    const ensureSpace = (h) => { if (y + h > BOTTOM) { doc.addPage(); y = 50; } };
    const sectionBar = (title) => {
      ensureSpace(90);
      doc.setFillColor(...GREEN); doc.roundedRect(M, y, W, 30, 6, 6, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(14);
      doc.text(title, M + 12, y + 20); y += 42;
    };
    const macrosBanner = () => {
      doc.setFillColor(236, 252, 203); doc.roundedRect(M, y, W, 26, 6, 6, "F");
      doc.setTextColor(...GREEN); doc.setFont("helvetica", "bold"); doc.setFontSize(10);
      const calR = t.calories_min && t.calories_max ? `${t.calories_min}–${t.calories_max}` : t.calories;
      const proR = t.protein_g_min != null ? `${t.protein_g_min}–${t.protein_g_max}` : t.protein_g;
      const gluR = t.carbs_g_min != null ? `${t.carbs_g_min}–${t.carbs_g_max}` : t.carbs_g;
      const lipR = t.fat_g_min != null ? `${t.fat_g_min}–${t.fat_g_max}` : t.fat_g;
      doc.text(`Par jour :  ${calR} kcal    ·    Protéines ${proR} g    ·    Glucides ${gluR} g    ·    Lipides ${lipR} g`, M + 12, y + 17);
      y += 40;
    };

    // ---------- Cover page ----------
    doc.setFillColor(10, 15, 13); doc.rect(0, 0, 595, 842, "F");
    doc.setFillColor(...LIME); doc.roundedRect(M, 60, 52, 52, 14, 14, "F");
    // little sprout
    doc.setFillColor(10, 15, 13);
    doc.ellipse(M + 20, 84, 7, 12, "F"); doc.ellipse(M + 33, 82, 7, 12, "F");
    doc.setDrawColor(10, 15, 13); doc.setLineWidth(2.5); doc.line(M + 26, 100, M + 26, 84);
    doc.setTextColor(...LIME); doc.setFont("helvetica", "bold"); doc.setFontSize(30); doc.text("Pousse", M + 68, 88);
    doc.setTextColor(200, 200, 200); doc.setFont("helvetica", "normal"); doc.setFontSize(11); doc.text("Ton coach sport & nutrition", M + 68, 106);

    doc.setFillColor(...LIME); doc.rect(M, 300, 60, 5, "F");
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(28);
    doc.text("Programme personnalisé", M, 350);
    const start = program.created_at ? new Date(program.created_at) : new Date();
    const end = new Date(start.getTime() + 28 * 86400000);
    const df = (d) => d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
    doc.setFont("helvetica", "normal"); doc.setFontSize(14); doc.setTextColor(220, 220, 220);
    const rows = [
      ["Prénom", user?.name || "—"],
      ["Objectif", GOAL_FR[program.goal] || p.overview || "Bien-être général"],
      ["Programme", `du ${df(start)} au ${df(end)}`],
      ["Cycle", String(program.cycle_number || 1)],
    ];
    let cy = 400;
    rows.forEach(([k, v]) => {
      doc.setTextColor(...LIME); doc.setFont("helvetica", "bold"); doc.text(`${k} :`, M, cy);
      doc.setTextColor(235, 235, 235); doc.setFont("helvetica", "normal");
      doc.text(String(v), M + 110, cy); cy += 30;
    });
    doc.setTextColor(120, 120, 120); doc.setFontSize(9);
    doc.text("Programme bien-être général, ne remplace pas l'avis d'un professionnel de santé.", M, 800);

    // ---------- Training: one section per week ----------
    (p.training?.weeks || []).forEach((w) => {
      doc.addPage(); y = 50;
      sectionBar(`Semaine ${w.week}${w.objectif ? " — " + w.objectif : ""}`);
      macrosBanner();
      const body = [];
      (w.days || []).forEach((d) => {
        (d.exercices || []).forEach((ex, idx) => {
          body.push([idx === 0 ? `${d.jour}${d.titre ? "\n" + d.titre : ""}` : "",
            ex.nom, String(ex.series ?? ""), String(ex.repetitions ?? ""), String(ex.repos ?? "")]);
        });
      });
      autoTable(doc, {
        ...tableOpts, startY: y,
        head: [["Jour", "Exercice", "Séries", "Répétitions", "Repos"]],
        body,
        columnStyles: { 0: { cellWidth: 95, fontStyle: "bold" }, 2: { cellWidth: 55, halign: "center" }, 3: { cellWidth: 75, halign: "center" }, 4: { cellWidth: 70, halign: "center" } },
      });
      y = doc.lastAutoTable.finalY + 20;
    });

    // ---------- Nutrition: one section, a table per day ----------
    doc.addPage(); y = 50;
    sectionBar("Alimentation — 7 jours");
    macrosBanner();
    (p.nutrition?.jours || []).forEach((j) => {
      ensureSpace(70);
      doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(12);
      doc.text(`${j.jour}${j.kcal_jour ? `  (~${j.kcal_jour} kcal)` : ""}`, M, y); y += 6;
      const body = [];
      (j.repas || []).forEach((r) => {
        (r.aliments || []).forEach((a, idx) => {
          body.push([idx === 0 ? `${r.type}${r.nom ? "\n" + r.nom : ""}` : "",
            a.aliment, a.grammage || "", String(a.kcal ?? "")]);
        });
      });
      autoTable(doc, {
        ...tableOpts, startY: y + 6,
        head: [["Repas", "Aliments", "Quantité", "Calories"]],
        body,
        columnStyles: { 0: { cellWidth: 120, fontStyle: "bold" }, 2: { cellWidth: 75, halign: "center" }, 3: { cellWidth: 65, halign: "center" } },
      });
      y = doc.lastAutoTable.finalY + 18;
    });

    // ---------- Shopping list ----------
    if ((p.nutrition?.liste_courses || []).length) {
      doc.addPage(); y = 50;
      sectionBar("Liste de courses");
      const body = [];
      (p.nutrition.liste_courses || []).forEach((c) => {
        body.push([{ content: c.categorie, colSpan: 1, styles: { fontStyle: "bold", fillColor: [236, 252, 203], textColor: GREEN } }, (c.aliments || []).join(", ")]);
      });
      autoTable(doc, { ...tableOpts, startY: y, head: [["Catégorie", "Aliments"]], body, columnStyles: { 0: { cellWidth: 150 } } });
    }

    doc.save("programme-pousse.pdf");
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-lime-400" /></div>;

  if (generating) {
    return (
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-8 text-center" data-testid="program-generating">
        <Loader2 className="h-8 w-8 animate-spin text-lime-400 mx-auto mb-4" />
        <h3 className="font-display text-lg font-bold mb-2">Génération en cours…</h3>
        <p className="text-sm text-white/50 max-w-xs mx-auto">Ton coach prépare ton entraînement sur 4 semaines et ton plan alimentaire sur 7 jours. Cela prend environ une minute. 🌱</p>
      </div>
    );
  }

  if (!program) {
    const expired = user?.access === "expired";
    if (!hasQuestionnaire) {
      return (
        <div data-testid="create-program-cta" className="rounded-2xl border border-lime-400/40 bg-lime-400/[0.06] p-8 text-center">
          <div className="h-14 w-14 rounded-2xl bg-lime-400/20 flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="h-7 w-7 text-lime-400" />
          </div>
          <h3 className="font-display text-2xl font-black mb-2">Crée ton programme</h3>
          <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">
            Réponds à quelques questions pour recevoir un programme d'entraînement et un plan alimentaire personnalisés sur 4 semaines.
          </p>
          <button data-testid="start-questionnaire-button" onClick={() => navigate("/questionnaire")}
            className="inline-flex items-center gap-2 bg-lime-400 text-black font-extrabold px-6 py-3.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all shadow-[0_0_20px_rgba(200,255,0,0.3)]">
            <Sparkles className="h-4 w-4" /> Commencer le questionnaire
          </button>
        </div>
      );
    }
    return (
      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-8 text-center">
        <div className="h-14 w-14 rounded-2xl bg-lime-400/15 flex items-center justify-center mx-auto mb-4">
          {expired ? <Lock className="h-7 w-7 text-lime-400" /> : <Sparkles className="h-7 w-7 text-lime-400" />}
        </div>
        <h3 className="font-display text-xl font-bold mb-2">{expired ? "Débloque ton programme" : "Génère ton programme"}</h3>
        <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">
          {expired ? "Ton essai gratuit est terminé. Abonne-toi pour générer ton programme personnalisé."
                   : "Lance ton essai gratuit de 7 jours : ton coach crée un plan d'entraînement et alimentaire sur mesure (4 semaines). La semaine 1 est offerte."}
        </p>
        {expired ? (
          <button data-testid="see-plans-button" onClick={() => navigate("/pricing")} className="inline-flex items-center gap-2 bg-lime-400 text-black font-extrabold px-6 py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">
            Voir les formules
          </button>
        ) : (
          <button data-testid="generate-program-button" onClick={generate} disabled={generating}
            className="inline-flex items-center gap-2 bg-lime-400 text-black font-extrabold px-6 py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all disabled:opacity-60">
            {generating ? <><Loader2 className="h-4 w-4 animate-spin" /> Génération…</> : <><Sparkles className="h-4 w-4" /> Lancer mon essai gratuit</>}
          </button>
        )}
      </div>
    );
  }

  if (program.access === "expired") {
    return (
      <div data-testid="trial-expired-card" className="rounded-2xl border border-white/10 bg-[#141C17] p-8 text-center">
        <div className="h-14 w-14 rounded-2xl bg-lime-400/15 flex items-center justify-center mx-auto mb-4">
          <Lock className="h-7 w-7 text-lime-400" />
        </div>
        <h3 className="font-display text-xl font-bold mb-2">Ton essai est terminé</h3>
        <p className="text-sm text-gray-400 mb-6 max-w-xs mx-auto">
          Abonne-toi pour débloquer tout ton programme (semaines 2 à 4, export PDF) et continuer à progresser.
        </p>
        <button data-testid="trial-subscribe-button" onClick={() => navigate("/pricing")}
          className="inline-flex items-center gap-2 bg-lime-400 text-black font-extrabold px-6 py-3 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">
          M'abonner
        </button>
      </div>
    );
  }

  const p = program.plan, t = p.targets || {};
  const weeks = p.training?.weeks || [];
  const trialDate = program.trial_ends_at ? new Date(program.trial_ends_at).toLocaleDateString("fr-FR") : "";
  const lockedWeeks = p.locked?.weeks || [];
  const pdfLocked = !!p.locked?.pdf;
  const lockMsg = `Disponible après ton essai gratuit, le ${trialDate}`;
  const jours = p.nutrition?.jours || [];
  const curWeek = weeks[week] || {};
  const curDay = jours[day] || {};

  return (
    <div className="space-y-4">
      <DisclaimerBanner />

      <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold text-lime-400">CYCLE {program.cycle_number}</span>
          {pdfLocked ? (
            <button data-testid="program-download-pdf-locked" onClick={() => toast.message(lockMsg)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/40 border border-white/15 rounded-lg px-3 py-1.5">
              <Lock className="h-4 w-4" /> PDF verrouillé
            </button>
          ) : (
            <button data-testid="program-download-pdf-button" onClick={downloadPdf}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-lime-300 border border-lime-400/30 rounded-lg px-3 py-1.5 hover:bg-lime-400/10">
              <Download className="h-4 w-4" /> Télécharger en PDF
            </button>
          )}
        </div>
        <p className="text-sm text-gray-300 leading-relaxed">{p.overview}</p>
      </div>

      {/* Targets */}
      <div data-testid="program-targets-card" className="rounded-2xl border border-lime-400/25 bg-lime-400/[0.05] p-5">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <p className="text-xs text-white/50 uppercase tracking-wide">Cible calorique / jour</p>
            <p className="font-display text-3xl font-black text-lime-400" data-testid="target-calories">
              {t.calories_min && t.calories_max ? `${t.calories_min}–${t.calories_max}` : (t.calories || "—")} <span className="text-base text-white/50">kcal</span>
            </p>
          </div>
          <span className="text-xs text-white/40">Activité : {t.activity_label} · {GOAL_FR[program.goal] || t.goal_label}</span>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[["Protéines", t.protein_g_min, t.protein_g_max, t.protein_g],
            ["Glucides", t.carbs_g_min, t.carbs_g_max, t.carbs_g],
            ["Lipides", t.fat_g_min, t.fat_g_max, t.fat_g]].map(([l, lo, hi, v]) => (
            <div key={l} className="rounded-xl bg-white/[0.04] border border-white/8 p-3 text-center">
              <p className="text-[10px] text-white/40 uppercase">{l}</p>
              <p className="text-lg font-black mt-0.5">{lo != null && hi != null ? `${lo}–${hi}` : (v ?? "—")} <span className="text-xs text-white/40">g</span></p>
            </div>
          ))}
        </div>
        <p className="text-[11px] text-white/30 mt-3">Calcul Mifflin-St Jeor · métabolisme {t.bmr} kcal · dépense estimée {t.tdee} kcal</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 bg-[#141C17] border border-white/10 rounded-xl p-1">
        {[["entrainement", "Entraînement", Dumbbell], ["alimentation", "Alimentation", Apple]].map(([k, l, Icon]) => (
          <button key={k} data-testid={`program-tab-${k}`} onClick={() => setTab(k)}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-semibold transition-all ${tab === k ? "bg-lime-400 text-black" : "text-white/60"}`}>
            <Icon className="h-4 w-4" /> {l}
          </button>
        ))}
      </div>

      {tab === "entrainement" && (
        <div className="space-y-4">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {weeks.map((w, i) => (
              <button key={i} data-testid={`program-week-tab-${i + 1}`} onClick={() => { setWeek(i); setLockedWeek(null); }}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border ${week === i && lockedWeek === null ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 text-white/60"}`}>
                Semaine {w.week || i + 1}
              </button>
            ))}
            {lockedWeeks.map((wn) => (
              <button key={`l${wn}`} data-testid={`program-week-locked-${wn}`} onClick={() => setLockedWeek(wn)}
                className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border ${lockedWeek === wn ? "border-lime-400/60 text-lime-300" : "border-white/10 text-white/40"}`}>
                <Lock className="h-3.5 w-3.5" /> Semaine {wn}
              </button>
            ))}
          </div>
          {lockedWeek !== null ? (
            <div data-testid="program-week-locked-panel" className="rounded-2xl border border-white/10 bg-[#141C17] p-8 text-center">
              <div className="h-12 w-12 rounded-2xl bg-lime-400/15 flex items-center justify-center mx-auto mb-3">
                <Lock className="h-6 w-6 text-lime-400" />
              </div>
              <h4 className="font-display font-bold mb-1">Semaine {lockedWeek} verrouillée</h4>
              <p className="text-sm text-white/50 mb-5">{lockMsg}</p>
              <button data-testid="locked-week-subscribe-button" onClick={() => navigate("/pricing")}
                className="inline-flex items-center gap-2 bg-lime-400 text-black font-extrabold px-5 py-2.5 rounded-xl hover:bg-[#b0e600] active:scale-95 transition-all">
                M'abonner pour débloquer
              </button>
            </div>
          ) : (
          <>
          {curWeek.objectif && (
            <div className="rounded-xl bg-lime-400/[0.06] border border-lime-400/20 px-4 py-3 text-sm text-lime-100">🎯 {curWeek.objectif}</div>
          )}
          <div className="space-y-3">
            {(curWeek.days || []).map((d, i) => (
              <div key={i} data-testid="program-day-card" className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2"><Flame className="h-4 w-4 text-lime-400" /><span className="font-semibold text-sm">{d.jour}</span></div>
                  <span className="text-xs text-lime-300">{d.titre}</span>
                </div>
                <div className="space-y-2">
                  {(d.exercices || []).map((ex, j) => (
                    <div key={j} className="border-b border-white/5 pb-2 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-gray-200">{ex.nom}</span>
                        <span className="text-white/40 text-xs whitespace-nowrap ml-2">{ex.series}×{ex.repetitions} · {ex.repos}</span>
                      </div>
                      {ex.conseil && <p className="text-[11px] text-white/30 mt-0.5">💡 {ex.conseil}</p>}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          </>
          )}
        </div>
      )}

      {tab === "alimentation" && (
        <div className="space-y-4">
          {p.nutrition?.note && <p className="text-xs text-white/50">{p.nutrition.note}</p>}
          <div className="flex gap-2 overflow-x-auto pb-1">
            {jours.map((j, i) => (
              <button key={i} data-testid={`program-nutrition-day-${i + 1}`} onClick={() => setDay(i)}
                className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border ${day === i ? "border-lime-400 bg-lime-400/10 text-lime-300" : "border-white/10 text-white/60"}`}>
                {j.jour || `Jour ${i + 1}`}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between rounded-xl bg-white/[0.03] border border-white/8 px-4 py-2.5">
            <span className="text-sm text-white/60">Total du jour (portions ajustées)</span>
            <span className="font-bold text-lime-300" data-testid="nutrition-day-total">
              ~{(curDay.repas || []).reduce((s, r, mi) => s + Math.round((getMeal(mi, r).kcal_total || 0) * getScale(mi)), 0)} kcal
            </span>
          </div>
          <div className="space-y-3">
            {(curDay.repas || []).map((r, i) => {
              const meal = getMeal(i, r); const f = getScale(i); const loading = subLoading === mkey(i);
              return (
                <div key={i} data-testid="program-meal-card" className="rounded-xl border border-white/8 bg-white/[0.02] p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <p className="text-xs font-bold text-lime-300">{meal.type}</p>
                      <p className="text-sm font-medium">{meal.nom}</p>
                    </div>
                    <span className="text-xs text-white/40 whitespace-nowrap">{Math.round((meal.kcal_total || 0) * f)} kcal</span>
                  </div>
                  <div className="space-y-1 mb-3">
                    {(meal.aliments || []).map((a, j) => (
                      <div key={j} className="flex items-center justify-between text-sm">
                        <span className="text-gray-300">{a.aliment}</span>
                        <span className="text-white/40 text-xs whitespace-nowrap ml-2">
                          <span className="text-lime-300/80 font-semibold">{scaleGram(a.grammage, f)}</span> · {Math.round((a.kcal || 0) * f)} kcal
                        </span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center justify-between border-t border-white/8 pt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-white/40 uppercase mr-1">Portion</span>
                      <button data-testid={`meal-portion-minus-${i}`} onClick={() => setScale(i, f - 0.1)}
                        className="h-7 w-7 rounded-lg border border-white/15 flex items-center justify-center hover:bg-white/5"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="text-xs font-semibold w-10 text-center" data-testid={`meal-portion-value-${i}`}>{Math.round(f * 100)}%</span>
                      <button data-testid={`meal-portion-plus-${i}`} onClick={() => setScale(i, f + 0.1)}
                        className="h-7 w-7 rounded-lg border border-white/15 flex items-center justify-center hover:bg-white/5"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button data-testid={`meal-substitute-${i}`} onClick={() => substituteMeal(i, meal)} disabled={loading}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold text-lime-300 border border-lime-400/30 rounded-lg px-3 py-1.5 hover:bg-lime-400/10 disabled:opacity-50">
                      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Shuffle className="h-3.5 w-3.5" />} Remplacer
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {(p.nutrition?.liste_courses || []).length > 0 && (
            <div data-testid="program-shopping-list" className="rounded-2xl border border-white/10 bg-[#141C17] p-5">
              <div className="flex items-center gap-2 mb-4"><ShoppingCart className="h-5 w-5 text-lime-400" /><h3 className="font-display font-bold">Liste de courses</h3></div>
              <div className="space-y-4">
                {p.nutrition.liste_courses.map((c, i) => (
                  <div key={i}>
                    <p className="text-xs font-bold text-lime-300 uppercase mb-1.5">{c.categorie}</p>
                    <div className="space-y-0.5">
                      {(c.aliments || []).map((a, j) => (
                        <button key={j} data-testid="shopping-item" onClick={() => toggleItem(a)}
                          className="flex items-center gap-2 text-sm w-full text-left py-1.5">
                          {checkedItems[a] ? <CheckSquare className="h-4 w-4 text-lime-400 shrink-0" /> : <Square className="h-4 w-4 text-white/30 shrink-0" />}
                          <span className={checkedItems[a] ? "line-through text-white/40" : "text-gray-300"}>{a}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-5">
                <button data-testid="shopping-export-button" onClick={exportShopping}
                  className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold border border-white/15 rounded-xl py-2.5 hover:bg-white/5">
                  <FileDown className="h-4 w-4" /> Exporter
                </button>
                <button data-testid="shopping-email-button" onClick={emailShopping}
                  className="flex-1 inline-flex items-center justify-center gap-2 text-sm font-extrabold bg-lime-400 text-black rounded-xl py-2.5 hover:bg-[#b0e600]">
                  <Mail className="h-4 w-4" /> Par email
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {history.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#141C17] p-5" data-testid="program-history">
          <div className="flex items-center gap-2 mb-4"><History className="h-5 w-5 text-lime-400" /><h3 className="font-display font-bold">Historique des cycles</h3></div>
          <div className="space-y-2">
            {history.map((h) => (
              <div key={h.id} className="rounded-xl border border-white/8 bg-white/[0.02] overflow-hidden">
                <button data-testid={`history-cycle-${h.cycle_number}`} onClick={() => setOpenCycle(openCycle === h.id ? null : h.id)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left">
                  <div>
                    <p className="text-sm font-semibold">Cycle {h.cycle_number}</p>
                    <p className="text-xs text-white/40">{new Date(h.created_at).toLocaleDateString("fr-FR")} · {GOAL_FR[h.goal] || h.goal}</p>
                  </div>
                  <ChevronDown className={`h-4 w-4 text-white/40 transition-transform ${openCycle === h.id ? "rotate-180" : ""}`} />
                </button>
                {openCycle === h.id && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/8">
                    <p className="text-xs text-gray-400 leading-relaxed mb-1">{h.plan?.overview}</p>
                    <p className="text-xs text-white/40">
                      Cible {h.plan?.targets?.calories || "—"} kcal · {(h.plan?.training?.weeks || []).length} semaines · {(h.plan?.nutrition?.jours || []).length} jours de repas
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
